import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import * as LanternApi from 'src/utilities/LanternApiClient';
import { useSnackbar } from 'notistack';
import { useRole } from './RoleContext';
import useAuth from 'src/hooks/useAuth';

const SelectedClientsContext = createContext();

export const useSelectedClients = () => {
  const ctx = useContext(SelectedClientsContext);
  if (!ctx) throw new Error('useSelectedClients must be used within SelectedClientsProvider');
  return ctx;
};

const storageKey = (role) => `lantern:selectedClients:${role || 'All'}`;
const recentlyCreatedClientsKey = (role) => `lantern:recentlyCreatedClients:${role || 'All'}`;
const recentlyCreatedTimestampsKey = (role) => `lantern:recentlyCreatedTimestamps:${role || 'All'}`;

// Keep recently created clients in localStorage for 10 minutes to prevent premature cleanup
// This allows time for backend propagation and reduces race conditions
const RECENTLY_CREATED_RETENTION_MS = 10 * 60 * 1000;

// Global and Administration pages
const pathnamesToExcludeFromUrlParamUpdate = [
  '/client-services-directory',
  '/client-services-directory/detail',
  '/client-offboarding-dashboard',
  '/client-offboarding-dashboard/detail',
  '/create-new-parent-client',
  '/onboarding-initiated',
  '/permissions',
  '/netsuite-client-mapping',
  '/forecast-roundtable-manager',
  '/client-manager',
  '/client-attribution-manager',
  '/pbcs-file-uploader',
  '/harvester-status',
];

export const SelectedClientsProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedRole } = useRole();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [availableClients, setAvailableClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const fetchInFlight = useRef(false);
  const lastFetchTime = useRef(0);
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const CACHE_DURATION_MS = 5000;

  const selectedClientIds = useMemo(() => selectedClients.map((c) => c.id), [selectedClients]);

  const refreshAvailableClients = useCallback(async () => {
    const now = Date.now();
    if (fetchInFlight.current) {
      return;
    }
    if (now - lastFetchTime.current < CACHE_DURATION_MS) {
      return;
    }

    fetchInFlight.current = true;
    lastFetchTime.current = now;
    setLoading(true);
    try {
      if (!user) {
        return;
      }

      const resp = await LanternApi.getParentClientsByUser(user);
      if (resp?.success) {
        const items = Array.isArray(resp.body) ? resp.body : [];

        const clients = items
          .map((c) => ({ id: c.id ?? c.parentClientId ?? c.Id, name: c.name ?? c.parentName ?? c.Name }))
          .filter((x) => x.id && x.name);

        // Load recently created clients from localStorage and merge with fetched clients
        const recentlyCreatedClientsJson = localStorage.getItem(recentlyCreatedClientsKey(selectedRole));
        const recentlyCreatedTimestampsJson = localStorage.getItem(recentlyCreatedTimestampsKey(selectedRole));

        let recentlyCreatedClients = [];
        let recentlyCreatedTimestamps = {};

        if (recentlyCreatedClientsJson) {
          try {
            recentlyCreatedClients = JSON.parse(recentlyCreatedClientsJson);
            if (!Array.isArray(recentlyCreatedClients)) {
              recentlyCreatedClients = [];
            }
          } catch (e) {
            console.warn('Failed to parse recently created clients from localStorage:', e);
            recentlyCreatedClients = [];
          }
        }

        if (recentlyCreatedTimestampsJson) {
          try {
            recentlyCreatedTimestamps = JSON.parse(recentlyCreatedTimestampsJson);
            if (
              typeof recentlyCreatedTimestamps !== 'object' ||
              recentlyCreatedTimestamps === null ||
              Array.isArray(recentlyCreatedTimestamps)
            ) {
              recentlyCreatedTimestamps = {};
            }
          } catch (e) {
            console.warn('Failed to parse recently created timestamps from localStorage:', e);
            recentlyCreatedTimestamps = {};
          }
        }

        // Clean up expired recently created clients (older than retention period)
        const now = Date.now();
        const validRecentClients = recentlyCreatedClients.filter((client) => {
          const timestamp = recentlyCreatedTimestamps[client.id];
          return timestamp && now - timestamp < RECENTLY_CREATED_RETENTION_MS;
        });

        // Merge recently created clients with fetched clients, removing duplicates
        const allClients = [...clients];
        validRecentClients.forEach((recentClient) => {
          if (!allClients.some((c) => c.id === recentClient.id)) {
            allClients.push(recentClient);
          }
        });

        // Sort the merged list
        allClients.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        // Only remove recently created clients that:
        // 1. Now exist in the API response (confirmed by backend)
        // 2. OR have exceeded the retention period
        const updatedRecentlyCreatedClients = validRecentClients.filter(
          (recentClient) => !clients.some((c) => c.id === recentClient.id),
        );

        // Update timestamps to only keep valid ones
        const updatedTimestamps = {};
        updatedRecentlyCreatedClients.forEach((client) => {
          if (recentlyCreatedTimestamps[client.id]) {
            updatedTimestamps[client.id] = recentlyCreatedTimestamps[client.id];
          }
        });

        // Save updated lists if they changed
        // Compare against validRecentClients since that's what we filtered from
        if (
          updatedRecentlyCreatedClients.length !== validRecentClients.length ||
          Object.keys(updatedTimestamps).length !== Object.keys(recentlyCreatedTimestamps).length
        ) {
          localStorage.setItem(recentlyCreatedClientsKey(selectedRole), JSON.stringify(updatedRecentlyCreatedClients));
          localStorage.setItem(recentlyCreatedTimestampsKey(selectedRole), JSON.stringify(updatedTimestamps));
        }

        setAvailableClients(allClients);

        // On initial load, restore selection with priority: URL param > localStorage > none
        if (!initialLoadComplete) {
          const clientIdParam = searchParams.get('clientId');
          let restoredSelection = [];

          if (clientIdParam) {
            // Try to restore from URL parameter (check both fetched and recently created clients)
            const clientId = parseInt(clientIdParam, 10);
            const client = allClients.find((c) => c.id === clientId);
            if (client) {
              restoredSelection = [client];
            }
          } else {
            // Fallback to localStorage
            const persisted = JSON.parse(localStorage.getItem(storageKey(selectedRole)) || '[]');
            if (Array.isArray(persisted) && persisted.length) {
              restoredSelection = allClients.filter((c) => persisted.includes(c.id));
            }
          }

          if (restoredSelection.length > 0) {
            setSelectedClients(restoredSelection);
          }
          setInitialLoadComplete(true);
        }
      } else {
        if (!resp) {
          // Likely in auth recovery; avoid noisy errors while sign-in flow re-establishes session.
          return;
        }
        const statusCode = Number(resp?.response?.status);
        if (statusCode === 401) {
          // Auth recovery is handled centrally; avoid flashing a noisy client-list error.
          return;
        }
        enqueueSnackbar('Failed to load your client list.', { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar('An unexpected error occurred loading clients.', { variant: 'error' });
    } finally {
      fetchInFlight.current = false;
      setLoading(false);
    }
  }, [enqueueSnackbar, selectedRole, user?.id, initialLoadComplete, searchParams]);

  // Load available clients on mount and when role changes
  useEffect(() => {
    setInitialLoadComplete(false);
    refreshAvailableClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  // Sync selection to URL params and localStorage
  useEffect(() => {
    if (!initialLoadComplete) return;

    // Update localStorage
    localStorage.setItem(storageKey(selectedRole), JSON.stringify(selectedClientIds));

    if (pathnamesToExcludeFromUrlParamUpdate.includes(location.pathname)) {
      return;
    }

    // Update URL parameter
    const currentClientId = searchParams.get('clientId');
    const newClientId = selectedClientIds.length === 1 ? String(selectedClientIds[0]) : null;

    if (newClientId !== currentClientId) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (newClientId) {
            newParams.set('clientId', newClientId);
          } else {
            newParams.delete('clientId');
          }
          return newParams;
        },
        { replace: true },
      );
    }
  }, [selectedClientIds, selectedRole, initialLoadComplete, searchParams, setSearchParams, location.pathname]);

  const clearSelected = useCallback(() => setSelectedClients([]), []);

  const setSelectedByIds = useCallback(
    (ids) => {
      const next = availableClients.filter((c) => ids.includes(c.id));
      setSelectedClients(next);
    },
    [availableClients],
  );

  // Add a recently created client and select it (persists across refreshes)
  const addRecentlyCreatedClient = useCallback(
    (client) => {
      if (!client?.id || !client?.name) {
        console.warn('addRecentlyCreatedClient requires client with id and name');
        return;
      }

      const normalizedClient = { id: client.id, name: client.name };

      setAvailableClients((prev) => {
        // Check if client already exists
        if (prev.some((c) => c.id === normalizedClient.id)) {
          return prev;
        }
        return [...prev, normalizedClient].sort((a, b) => a.name.localeCompare(b.name));
      });

      // Save to localStorage so it persists across refreshes
      const recentlyCreatedClientsJson = localStorage.getItem(recentlyCreatedClientsKey(selectedRole));
      const recentlyCreatedTimestampsJson = localStorage.getItem(recentlyCreatedTimestampsKey(selectedRole));

      let recentlyCreatedClients = [];
      let recentlyCreatedTimestamps = {};

      if (recentlyCreatedClientsJson) {
        try {
          recentlyCreatedClients = JSON.parse(recentlyCreatedClientsJson);
          if (!Array.isArray(recentlyCreatedClients)) {
            recentlyCreatedClients = [];
          }
        } catch (e) {
          recentlyCreatedClients = [];
        }
      }

      if (recentlyCreatedTimestampsJson) {
        try {
          recentlyCreatedTimestamps = JSON.parse(recentlyCreatedTimestampsJson);
          if (
            typeof recentlyCreatedTimestamps !== 'object' ||
            recentlyCreatedTimestamps === null ||
            Array.isArray(recentlyCreatedTimestamps)
          ) {
            recentlyCreatedTimestamps = {};
          }
        } catch (e) {
          recentlyCreatedTimestamps = {};
        }
      }

      // Add to recently created clients if not already there
      if (!recentlyCreatedClients.some((c) => c.id === normalizedClient.id)) {
        recentlyCreatedClients.push(normalizedClient);
        recentlyCreatedTimestamps[normalizedClient.id] = Date.now();

        localStorage.setItem(recentlyCreatedClientsKey(selectedRole), JSON.stringify(recentlyCreatedClients));
        localStorage.setItem(recentlyCreatedTimestampsKey(selectedRole), JSON.stringify(recentlyCreatedTimestamps));
      }

      // Always select this client (even if another client is currently selected)
      setSelectedClients([normalizedClient]);

      // Force update the URL parameter immediately, preserving existing location state
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set('clientId', String(normalizedClient.id));
          return newParams;
        },
        { replace: true, state: locationRef.current.state },
      );
    },
    [selectedRole, setSearchParams],
  );

  const value = {
    // Data
    availableClients,
    selectedClients,
    selectedClientIds,
    loadingAvailableClients: loading,

    // Actions
    refreshAvailableClients,
    setSelectedClients,
    setSelectedByIds,
    clearSelected,
    addRecentlyCreatedClient,
  };

  return <SelectedClientsContext.Provider value={value}>{children}</SelectedClientsContext.Provider>;
};

export default SelectedClientsContext;
